import startOfDay from 'date-fns/startOfDay'
import * as React from 'react'
import { useHistory } from 'react-router'
import * as TicketSizesActions from '../../../../actions/ticketSizes'
import { TicketSizeCreateAction, TicketSizeCreation } from '../../../../actions/ticketSizes/createTicketSize'
import { TicketSizeUpdateAction } from '../../../../actions/ticketSizes/updateTicketSize'
import { useAppDispatch } from '../../../../actions/types'
import { Routes } from '../../../../constants'
import type { TicketSize } from '../../../../types'
import type { TicketSizeFormData } from '../types'
import * as Validation from '../validation'
import useTicketSizeParams from '../useTicketSizeParams'

const DEFAULT_SIZE_OPTION = 'ticket-large'

export default function useTicketSizeFormData(ticketSize?: TicketSize) {
    const dispatch = useAppDispatch()
    const history = useHistory()
    const { estate } = useTicketSizeParams()
    const [ticketSizeData, setTicketSizeData] = React.useState<TicketSizeFormData>({
        size: DEFAULT_SIZE_OPTION,
        start_date: startOfDay(new Date()),
        end_date: startOfDay(new Date(Date.now() + 1000 * 60 * 60 * 24 * 7)),
        skus: [],
        storecodes: [],
    })
    const [saveProgress, setSaveProgress] = React.useState<undefined | number>()
    const [areUnsavedChanges, setUnsavedChanges] = React.useState(false)
    const [formErrors, setFormErrors] = React.useState<Validation.TicketSizeValidation>({})
    const [submitError, setSubmitErrors] = React.useState<Error>()

    React.useEffect(() => {
        const isNew = !ticketSize

        if (isNew) {
            setTicketSizeData(prev => ({
                ...prev,
                size: DEFAULT_SIZE_OPTION,
            }))
        } else {
            setTicketSizeData(prev => ({
                ...prev,
                ...ticketSize,
            }))
        }
    }, [ticketSize])

    const isNew = !ticketSize

    const thereAreErrors =
        (validation: Validation.TicketSizeValidation) => Object.keys(validation).length > 0

    const createTicketSize = React.useCallback(async (ticketSizeData: TicketSizeFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const createAction: TicketSizeCreateAction = await dispatch(
            TicketSizesActions.createTicketSize(ticketSizeData as TicketSizeCreation)
        )

        if (createAction.type !== 'TICKET_SIZE_CREATE##SUCCESS') {
            setSubmitErrors(createAction.payload)
            setSaveProgress(undefined)
            return
        }

        const { id: ticketSizeId } = createAction.payload
        setSaveProgress(1)

        history.replace(
            Routes.buildRoute(
                Routes.SELF_SERVE_TICKET_SIZES,
                { estate, ticketSize: ticketSizeId },
            )
        )
    }, [dispatch, estate, history])

    const updateTicketSize = React.useCallback(async (ticketSizeData: TicketSizeFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const updateAction: TicketSizeUpdateAction = await dispatch(
            TicketSizesActions.updateTicketSize(ticketSizeData as TicketSize)
        )

        if (updateAction.type !== 'TICKET_SIZE_UPDATE##SUCCESS') {
            setSubmitErrors(updateAction.payload)
            setSaveProgress(undefined)
            return
        }

        setSaveProgress(1)
    }, [dispatch])

    const onSubmit = React.useCallback(() => {
        setFormErrors({})
        setUnsavedChanges(false)

        const errors = isNew
            ? Validation.validateCreation(ticketSizeData)
            : Validation.validateUpdate(ticketSizeData)

        if (thereAreErrors(errors)) {
            setFormErrors(errors)
            return
        }

        const saveAction = isNew ? createTicketSize : updateTicketSize

        saveAction(ticketSizeData).then(
            () => setTimeout(() => setSaveProgress(undefined), 5000),
        )
    }, [createTicketSize, isNew, ticketSizeData, updateTicketSize])

    return {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        ticketSizeData,
        saveProgress,
        setFormErrors,
        setSubmitErrors,
        setTicketSizeData,
        setUnsavedChanges,
        submitError,
    }
}
